import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { custromMaterialsGroups } from '@shared/models/consts';
import {
	CustomEntityForSave,
	CustomMaterialApi,
	CustomMaterialEntitiyUniqueId,
	WithCustomMaterialApiProps,
	WithCustomMaterialParsedProps
} from '@shared/models/custom-material';
import { ToothApi } from '@modules/teeth-diagram/models/tooth-api';
import { getToothWithReplacedForRxAppDefault } from '@shared/models/Mappers/default-values-mapper';
import type { Maybe } from '@/types/globals';

export const encodeCustomMaterialId = (material: CustomMaterialEntitiyUniqueId): string => {
	return `customMaterial_${material?.id}_rev_${material?.lastRevisionId}_lab_${material?.labId}`;
};

export const encodeCustomSpecificationId = (specification: CustomMaterialEntitiyUniqueId): string => {
	return `customSpecification_${specification?.id}_rev_${specification?.lastRevisionId}_lab_${specification?.labId}`;
};

export const decodeCustomMaterialEntityId = (encodedEntityId: string | number): CustomMaterialEntitiyUniqueId => {
	const [, sId, , sLastRevisionId, , sLabId] = String(encodedEntityId).split('_');
	const pId = Number.parseInt(sId, 10);
	const pLastRevisionId = Number.parseInt(sLastRevisionId, 10);
	const pLabId = Number.parseInt(sLabId, 10);

	return {
		id: Number.isNaN(pId) ? undefined : pId,
		lastRevisionId: Number.isNaN(pLastRevisionId) ? undefined : pLastRevisionId,
		labId: Number.isNaN(pLabId) ? undefined : pLabId
	};
};

export const customMaterialEntityApiToParsedMapper = (
	entity: WithCustomMaterialApiProps | CustomMaterialApi,
	labId: number
): WithCustomMaterialParsedProps => ({
	labId,
	isAvailable: entity.isActive && !entity.isRemoved,
	name: entity.customName
		? `${
				'materialGroup' in entity && entity.materialGroup !== custromMaterialsGroups.Composites ? `${entity.materialGroup}: ` : ''
			}${entity.customName}`
		: entity.localizedName
});

export const getOriginalRxMaterialFromTeeth = (materialId: number | string, teeth: Tooth[]): CustomEntityForSave | undefined => {
	return (teeth ?? [])
		.filter((tooth): tooth is Tooth & { CustomLabMaterialId: number } => !!tooth.CustomLabMaterialId)
		.map(tooth => ({
			id: tooth.CustomLabMaterialId,
			name: tooth.CustomLabMaterialName as string,
			lastRevisionId: tooth.CustomLabMaterialRevisionId as number,
			materialGroup: tooth.CustomLabMaterialGroup ?? undefined
		}))
		.find(material => {
			const decodedId = decodeCustomMaterialEntityId(materialId);

			return material.id === decodedId.id && material.lastRevisionId === decodedId.lastRevisionId;
		});
};

export const getOriginalRxSpecificationFromTeeth = (specificationId: number | string, teeth: Tooth[]): CustomEntityForSave | undefined => {
	return (teeth ?? [])
		.filter((tooth): tooth is Tooth & { CustomLabSpecificationId: number } => !!tooth.CustomLabSpecificationId)
		.map(tooth => ({
			id: tooth.CustomLabSpecificationId,
			name: tooth.CustomLabSpecificationName as string,
			lastRevisionId: tooth.CustomLabSpecificationRevisionId as number,
			materialGroup: tooth.CustomLabMaterialGroup ?? undefined
		}))
		.find(specification => {
			const decodedId = decodeCustomMaterialEntityId(specificationId);

			return specification.id === decodedId.id && specification.lastRevisionId === decodedId.lastRevisionId;
		});
};

export const convertToothApiToTooth = (tooth: ToothApi, labId: Maybe<number>): Tooth => {
	const convertedTooth = {
		...tooth,
		...((tooth?.CustomLabSpecificationId
			? {
					SpecificationId: encodeCustomSpecificationId({
						id: tooth.CustomLabSpecificationId,
						lastRevisionId: tooth.CustomLabSpecificationRevisionId,
						labId
					})
				}
			: {}) satisfies Partial<Tooth>),
		...((tooth?.CustomLabMaterialId
			? {
					MaterialID: encodeCustomMaterialId({
						id: tooth.CustomLabMaterialId,
						lastRevisionId: tooth.CustomLabMaterialRevisionId,
						labId
					})
				}
			: {}) satisfies Partial<Tooth>)
	} satisfies Tooth;

	return getToothWithReplacedForRxAppDefault(convertedTooth);
};
